/**
 * The enlarged example clip: a dialog over the inert stage holding the same
 * `<video>` the frame showed (moved here through its slot, returned on
 * close), the play and sound controls with visible labels, and the note.
 *
 * Same rules as the request-access dialog: `role="dialog"`, `aria-modal`,
 * initial focus on the play button, Tab trapped, Escape closes, the scrim
 * closes only for a pointer that goes down and up on it, and the page
 * returns focus to the enlarge button that opened it.
 */

import { useRef, type PointerEvent, type ReactNode } from 'react';

import { lightboxNote, type PublicEarlyAccessCopy } from './copy.js';
import type { ExampleVideo } from './example-video.js';
import { PlayPauseIcon, SoundIcon } from './icons.js';
import { useDialogFocus } from './useDialogFocus.js';

export const LIGHTBOX_IDS = {
  play: 'cs-lb-play',
  sound: 'cs-lb-sound',
} as const;

export function VideoLightbox({
  copy,
  video,
  onClose,
}: {
  readonly copy: PublicEarlyAccessCopy;
  readonly video: ExampleVideo;
  readonly onClose: () => void;
}): ReactNode {
  const card = useRef<HTMLDivElement | null>(null);
  const scrimDown = useRef(false);

  useDialogFocus({
    card,
    initialFocus: () => document.getElementById(LIGHTBOX_IDS.play),
    onEscape: onClose,
  });

  const onBackdropPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    scrimDown.current = event.target === event.currentTarget;
  };
  const onBackdropPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (scrimDown.current && event.target === event.currentTarget) onClose();
    scrimDown.current = false;
  };

  return (
    <div
      className="cs-access cs-lightbox"
      data-open="true"
      onPointerDown={onBackdropPointerDown}
      onPointerUp={onBackdropPointerUp}
    >
      <div
        ref={card}
        className="cs-lightbox-card"
        role="dialog"
        aria-modal="true"
        aria-label={copy.fTitle}
      >
        <button
          type="button"
          className="cs-access-close cs-lightbox-close"
          aria-label={copy.closeEnlarge}
          onPointerDown={(event) => {
            event.stopPropagation();
          }}
          onClick={onClose}
        >
          ×
        </button>
        <div className="cs-lightbox-frame" ref={video.enlargedHost} />
        <div className="cs-lightbox-controls">
          <button
            type="button"
            className="cs-lb-btn cs-play-toggle"
            id={LIGHTBOX_IDS.play}
            aria-pressed={video.playing}
            aria-label={video.playing ? copy.pause : copy.play}
            onClick={video.togglePlay}
          >
            <PlayPauseIcon />
            <span>{video.playing ? copy.lbPause : copy.lbPlay}</span>
          </button>
          <button
            type="button"
            className="cs-lb-btn cs-sound-toggle"
            id={LIGHTBOX_IDS.sound}
            aria-pressed={!video.muted}
            aria-label={video.muted ? copy.unmute : copy.mute}
            onClick={video.toggleSound}
          >
            <SoundIcon />
            <span>{video.muted ? copy.lbSoundOn : copy.lbSoundOff}</span>
          </button>
        </div>
        <p className="cs-lightbox-note">{lightboxNote(copy)}</p>
      </div>
    </div>
  );
}
